import type { MaybeRefOrGetter } from 'vue';
import type { UseBlockStorageKeyOptions } from './use-block-storage-key';
import type { UsePersistedStateOptions } from './use-persisted-state';
import { useBlockInEditorContext } from '#layers/block-engine';
import { useBlockStorageKey } from '~/composables/use-block-storage-key';
import { usePersistedState } from '~/composables/use-persisted-state';

export interface UsePersistedBlockStateOptions extends Omit<UsePersistedStateOptions, 'blockId' | 'ownerId'>, UseBlockStorageKeyOptions {}

/**
 * Manages a reactive state with persistence, scoped by block context, user, and collection details.
 *
 * @param {MaybeRefOrGetter<string>} key - The base key used for identifying the persisted state.
 * @param {MaybeRefOrGetter<T>} defaults - The default value to use if no existing persisted value is found.
 * @param {UsePersistedBlockStateOptions} [options] - Optional configuration for the persisted state, such as scope, collection IDs, or other settings.
 * @return {ReturnType<typeof usePersistedState>} A reactive state object that manages persisted values according to the computed storage key based on context.
 */
export function usePersistedBlockState<T>(key: MaybeRefOrGetter<string>, defaults: MaybeRefOrGetter<T>, options?: UsePersistedBlockStateOptions) {
	const { writeDefaults } = options ?? {};
	const { blockInEditor } = useBlockInEditorContext();

	const { storageKey, rootBlockId, collectionId, ownerId } = useBlockStorageKey(key, {
		collectionId: options?.collectionId,
		scope: options?.scope,
	});

	return usePersistedState(storageKey, defaults, {
		...options,
		blockId: rootBlockId,
		collectionId,
		ownerId,
		writeDefaults: writeDefaults ?? !blockInEditor,
	});
}
